import React, { Component } from 'react';
import { Button } from '@trevor/ui-components';
import { get, post, userAction } from '@/util/helpers/ajax-helpers';
import { getUniques } from '@/util/helpers/array-helpers';
import { safeOnClick } from '@/util/helpers/event-helpers';
import TableRef from '../../util/TableRef';
import Checkbox from './Checkbox';
import ConfigurePage from './ConfigurePage';
import style from '../../css/module/tables.css';
import flex from '../../css/module/flex.css';
import SchemaColumn from '../../util/SchemaColumn';
import SearchInput from '../SearchInput';
import convertColumnTypeToDisplayName from '../../util/ColumnTypeToDisplayNameConverter';

class SelectTables_LEGACY extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      // eslint-disable-next-line react/no-unused-state
      firstTime: true, // true if first time configuring this datasource (meaning we'll skip configure JOINs)
      tables: [], // an array of TableRef objects
      selectedSchemas: [], // an obj showing unique schema names and a boolean showing if they're selected
      selectedTables: [], // an array of booleans to show which tables are selected
      columns: [], // an array of arrays of SchemaColumn objects - one array for each table in the tables state above.
      visibleColumns: [], // an array of booleans to show if the columns for each table in the tables state are visible
      selectedColumns: [], /// / an array of arrays of booleans - one array for each table
      loadError: false,
      // eslint-disable-next-line react/no-unused-state
      saveError: false,
      savingInProcess: false,
      searchQuery: '',
    };
    this.selectTable = this.selectTable.bind(this);
    this.toggleVisibleColumns = this.toggleVisibleColumns.bind(this);
    this.selectColumn = this.selectColumn.bind(this);
    this.selectAllColumns = this.selectAllColumns.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.onSave = this.onSave.bind(this);
    this.getTables = this.getTables.bind(this);
    this.onCancel = this.onCancel.bind(this);
  }

  componentDidMount() {
    // first load all existing tables and columns
    userAction('visit-customize-datasource-tables');
    this.getTables();
  }

  getTables() {
    if (this.state.loadError) {
      return; // stop loading
    }
    get('loading table list', '/configuration?includeHidden=true')
      .then((jsonWithHidden) => {
        // check still loading
        if (jsonWithHidden._stillLoading) {
          // metadata hasn't loaded yet, so wait 1 second and retry
          setTimeout(() => this.getTables(), 1000);
          return;
        }

        const allTables = jsonWithHidden._tables.map((t) =>
          TableRef.buildFromServer(t),
        );
        this.setState({
          tables: allTables,
          visibleColumns: jsonWithHidden._tables.map(() => false),
          columns: jsonWithHidden._tableColumns.map((cols) =>
            cols.map((c) => new SchemaColumn(c._name, c._type)),
          ),
        });
        // then load just the pre-selected tables columns (so we can show them selected)
        get('loading table list', '/configuration?includeHidden=false')
          .then((jsonWithoutHidden) => {
            const tableNamesOfSelected = jsonWithoutHidden._tables.map((t) =>
              TableRef.buildFromServer(t).uniqueName(),
            );
            const asSet = new Set(tableNamesOfSelected);
            const checkedTables = jsonWithHidden._tables
              .map((t) => TableRef.buildFromServer(t))
              .map((t) => asSet.has(t.uniqueName()));
            const selectedColumnNamesByTable = {};
            jsonWithoutHidden._tableColumns.map((cols, i) => {
              const tName = tableNamesOfSelected[i];
              selectedColumnNamesByTable[tName] = new Set(
                cols.map((c) => c._name),
              );
            });
            const selectedColumns = jsonWithHidden._tableColumns.map(
              (cols, i) =>
                cols.map((c) => {
                  const t = allTables[i];
                  const tColumns = selectedColumnNamesByTable[t.uniqueName()];
                  return tColumns && tColumns.has(c._name);
                }),
            );
            this.setState({
              // eslint-disable-next-line react/no-unused-state
              firstTime: jsonWithoutHidden._tables.length === 0,
              loading: false,
              selectedTables: checkedTables,
              selectedColumns,
            });
            setTimeout(() => {
              if (this._input) {
                this._input.focus();
              }
            }, 100);
          })

          .catch(() => {
            userAction(
              'problem-loading-preselected-columns-customize-datasource',
            );
            // force intercom to trigger a ping
            setTimeout(function () {
              const ts = Math.round(new Date().getTime() / 1000);
              Intercom('update', { dummy_attribute: ts });
            }, 3000);
            this.setState({
              loadError: true,
              loading: false,
            });
          });
      })
      .catch(() => {
        userAction('problem-loading-tables-page-customize-datasource');
        // force intercom to trigger a ping
        setTimeout(function () {
          const ts = Math.round(new Date().getTime() / 1000);
          Intercom('update', { dummy_attribute: ts });
        }, 3000);
        this.setState({
          loadError: true,
          loading: false,
        });
      });
    // returns unique schema names
    const schemas = getUniques(this.state.tables, (a) => a.schema());
    const obj = {};
    schemas.map((s) => (obj[s] = true)); // initialised to true (otherwise you have to click twice to activate, and it doesn't matter since no tables get selected unless columns are selected)
    this.setState({
      selectedSchemas: obj,
    });
  }

  // map through tables
  displayTables() {
    const { tables, selectedTables, columns, visibleColumns } = this.state;

    if (tables.length === 0) {
      return (
        <div style={{ padding: '30px', textAlign: 'center' }}>
          <h4>No tables found.</h4>
          <p style={{ paddingTop: '10px' }}>
            Trevor.io is a <b>read-only</b> data tool,
            <br />
            empowering your team to get answers
            <br />
            from your <b>existing</b> live database data.
          </p>
          <p>Please connect a different database.</p>
        </div>
      );
    }

    // returns unique schema names
    const schemas = getUniques(tables, (a) => a.schema());

    // if schemas contains 'public', put it at the front
    const first = 'public';
    schemas.sort(function (x, y) {
      return x === first ? -1 : y === first ? 1 : 0;
    });

    const displaySchemas = schemas.map((schema, pos) => {
      const displayTables = tables
        .filter((t) => t.schema() === schema)
        .map((t) => ({ t }))
        .filter((obj) => {
          const { t } = obj;
          const { searchQuery } = this.state;
          const nameAndSchema = (t.name() + t.schema()).toLowerCase();
          if (searchQuery.length > 2) {
            if (nameAndSchema.includes(searchQuery.toLowerCase().trim())) {
              return true;
            }
            return false;
          }
          return true;
        })
        .map((obj) => {
          const { t } = obj;
          const index = tables.indexOf(t);
          const isSelected = selectedTables[index]; // checks corresponding value from selected tables state
          const showColumns = visibleColumns[index];
          const sticky = showColumns ? style.tableSticky : '';
          return (
            <div
              className={`${style.tableRow} ${flex.flex} ${flex.column} w-100`}
              key={`table_${index}`}
            >
              <div
                className={`${style.table} ${sticky} ${flex.flex} w-100`}
                onClick={safeOnClick(() => this.selectTable(index))}
              >
                <div
                  className={`${flex.flex} ${flex.shrink} ${flex.justifyContentCenter} ${flex.alignItemsCenter}`}
                >
                  <Checkbox
                    selected={isSelected}
                    handleChange={() => this.selectTable(index)}
                  />
                </div>
                <div
                  className={`${flex.flex} ${flex.alignItemsCenter} ${flex.flexGrow}`}
                >
                  <div className={style.tableTag}>table</div>
                  <div className={style.tableName}>{t.name()}</div>
                  <small
                    className={`${style.columnToggleText} ${style.link}`}
                    onClick={(e) => this.toggleVisibleColumns(e, index)}
                  >
                    {!showColumns ? '(Show columns)' : '(Hide columns)'}
                  </small>
                </div>
                <div
                  className={`${flex.flex} ${flex.shrink} ${flex.justifyContentCenter} ${flex.alignItemsCenter}`}
                >
                  <span>{isSelected ? 'Selected' : 'Not selected'}</span>
                </div>
              </div>
              {!showColumns ? null : (
                <div>{this.displayColumns(columns[index], index)}</div>
              )}
            </div>
          );
        });

      const toggleSchema = this.state.selectedSchemas[schema]
        ? 'Deselect'
        : 'Select';

      return (
        <div
          key={`schema_${pos}`}
          className={`${flex.flex} ${flex.column} w-100`}
        >
          <div
            className={`${style.schemaRow} ${style.sticky} ${flex.flex} ${flex.alignItemsCenter} w-100`}
          >
            <div className={style.schemaTag}>Schema</div>
            <span>
              <b>{schema}</b>
            </span>
            <Button
              variant="secondary"
              size="small"
              onClick={(e) => this.selectSchema(e, schema)}
              style={{ marginLeft: '10px' }}
            >
              {toggleSchema} all tables
            </Button>
          </div>
          {displayTables}
        </div>
      );
    });

    return displaySchemas;
  }

  // display the columns underneath a table
  displayColumns(columns, position) {
    const tablePosition = this.state.selectedColumns[position];
    const display = columns.map((c, i) => {
      return (
        <div className={style.columnRow} key={`table_${position}_column_${i}`}>
          <div
            className={`${style.columnInnerPadding} ${flex.flex} w-100`}
            onClick={() => this.selectColumn(i, position)}
          >
            <div
              className={`${flex.flex} ${flex.shrink}`}
              style={{ marginRight: '8px' }}
            >
              <Checkbox selected={tablePosition[i]} />
            </div>
            <div
              className={`${flex.flex} ${flex.alignItemsCenter} ${flex.flexGrow}`}
            >
              <div className={style.columnName}>{c.name()}</div>
              <div className={`${style.columnType} text-muted`}>
                (TYPE: {convertColumnTypeToDisplayName(c.type())})
              </div>
            </div>
            <div className={`${flex.flex} ${flex.shrink}`}>
              {tablePosition[i] ? 'selected' : 'Not selected'}
            </div>
          </div>
        </div>
      );
    });
    return display;
  }

  // show and hide columns for a specific table when toggle columns clicked
  toggleVisibleColumns(e, position) {
    e.stopPropagation();
    const copyOfArray = this.state.visibleColumns.slice();
    const value = copyOfArray[position];
    copyOfArray[position] = !value;
    this.setState({
      visibleColumns: copyOfArray,
    });
  }

  selectSchema(e, schema) {
    if (e) {
      e.stopPropagation();
    }
    const { tables, selectedSchemas } = this.state;
    // toggle by taking opposite value of selectedSchemas state (inialised to false for each schema)
    const copyOfSelectedSchemas = { ...selectedSchemas };
    const value = !copyOfSelectedSchemas[schema];
    // take a copy of the tables and columns
    copyOfSelectedSchemas[schema] = value;
    const copyOfSelectedColumns = this.state.selectedColumns.slice();
    const copyOfSelectedTables = this.state.selectedTables.slice();
    // for a given schema, select all the tables and columns
    tables
      .filter((t) => t.schema() === schema)
      .map((t) => {
        const position = tables.indexOf(t);
        copyOfSelectedTables[position] = value;
        const allTrue = copyOfSelectedColumns[position].map(() => value);
        copyOfSelectedColumns[position] = allTrue;
      });
    this.setState({
      selectedTables: copyOfSelectedTables,
      selectedColumns: copyOfSelectedColumns,
      selectedSchemas: copyOfSelectedSchemas,
    });
  }

  // check and uncheck tables
  selectTable(position) {
    const copyOfSelected = this.state.selectedTables.slice();
    const value = copyOfSelected[position];
    copyOfSelected[position] = !value;
    this.setState({
      selectedTables: copyOfSelected,
    });
    // select all of a tables columns when that table is selected
    this.selectAllColumns(position, !value);
  }

  // check and uncheck columns
  selectColumn(columnPosition, tablePosition, e) {
    if (e) {
      e.stopPropagation();
    }
    // if a column is selected from a non-selected table, select that table
    if (this.state.selectedTables[tablePosition] === false) {
      this.selectTable(tablePosition);
    }
    const copyOfArray = this.state.selectedColumns.slice();
    const value = copyOfArray[tablePosition][columnPosition];
    copyOfArray[tablePosition][columnPosition] = !value;
    this.setState({
      selectedColumns: copyOfArray,
    });
  }

  selectAllColumns(tablePosition, value) {
    const copyOfArray = this.state.selectedColumns.slice();
    const allTrue = copyOfArray[tablePosition].map(() => value);
    copyOfArray[tablePosition] = allTrue;
    this.setState({
      selectedColumns: copyOfArray,
    });
  }

  refreshMeta() {
    this.setState({
      loading: true,
    });
    post('refreshing metadata', '/configuration/refresh')
      .then(() => {
        // refresh successful, reload tables
        userAction('refresh-datasource-tables-metadata');
        this.getTables();
      })
      .catch(() => {
        this.setState({
          loadError: true,
          loading: false,
        });
      });
  }

  onSave() {
    this.setState({
      savingInProcess: true,
    });
    const selectedTables = this.state.tables.filter(
      (t, i) => this.state.selectedTables[i],
    );
    const selectedColumns = this.state.columns
      .map((cols, i) => cols.filter((c, j) => this.state.selectedColumns[i][j]))
      .filter((cols, i) => this.state.selectedTables[i]);

    post('saving table selection', '/configuration/tables', {
      selectedTables,
      selectedColumns,
    })
      .then(() => {
        // successful
        if (!this.props.isModal) {
          // redirect to datasource
          window.location.href =
            /* json.isErDiagram
					? '../#m=ap'// only add #m=ap if referrer is https://trevor.io/create-an-er-diagram-online/
					: */ '../';
        } else {
          // reload schema (no page load so needs to trigger here) and close modal
          this.props.reloadSchema();
          this.props.closeModal();
        }
        userAction('click-save-datasource-tables-configuration', {
          selectedTables,
          selectedColumns,
        });
      })
      // TODO: handle this error properly
      .catch(() => {
        userAction('error-saving-tables-selection-setup-page');
        this.setState({
          // eslint-disable-next-line react/no-unused-state
          saveError: true,
          savingInProcess: false,
        });
        setTimeout(() => {
          this.setState({
            // eslint-disable-next-line react/no-unused-state
            saveError: false,
          });
        }, 4000);
      });
  }

  handleSearch(value) {
    const v = value.replace(' ', '_');
    this.setState({
      searchQuery: v,
    });
  }

  onCancel() {
    if (this.props.isModal) {
      this.props.closeModal();
    }
  }

  render() {
    const { isModal } = this.props;

    const disabled =
      !this.state.selectedTables.includes(true) || this.state.savingInProcess;

    const controlSection = (
      <div
        className={`${style.controlContainer} ${flex.row} ${flex.alignItemsEnd} ${flex.justifyContentBetween}`}
      >
        <SearchInput
          value={this.state.searchQuery}
          onSearch={this.handleSearch}
          placeholder="Search schemas and tables"
        />
        <div className={`${flex.flex} ${flex.alignItemsCenter}`}>
          <div className={style.tableCount}>
            {this.state.selectedTables.filter((t) => t).length} of{' '}
            {this.state.tables.length} selected
          </div>
          <Button variant="secondary" onClick={() => this.refreshMeta()}>
            Refresh metadata
          </Button>
        </div>
      </div>
    );

    return (
      <ConfigurePage
        showLoadingMessage
        disabled={disabled}
        controlSection={controlSection}
        onSave={this.onSave}
        onForceRefresh={() => this.refreshMeta()}
        saveButtonText={isModal ? 'Save selection' : 'Save and finish'}
        title="Choose the tables you want your team to see"
        cancel={this.onCancel}
        cancelButtonShown={isModal}
        loading={this.state.loading}
        loadError={this.state.loadError}
        cancelText={isModal ? 'Cancel' : 'Back to datasources'}
        justifyCenter={!isModal}
      >
        {this.displayTables()}
      </ConfigurePage>
    );
  }
}

export default SelectTables_LEGACY;
